// Session times are stored without a timezone and are Eastern.
// en-CA renders "a.m."; en-US renders "AM", which is the wording we want.

type Lang = 'en' | 'fr';

const timeLocale = (lang: Lang) => (lang === 'fr' ? 'fr-CA' : 'en-US');

export const tzLabel = (lang: Lang) => (lang === 'fr' ? 'HE' : 'ET');

/** Clock reading without AM/PM: "8:15", or "8 h 15" in French. */
function bare(d: Date, lang: Lang): string {
  const s = d.toLocaleTimeString(timeLocale(lang), { hour: 'numeric', minute: '2-digit' });
  return lang === 'fr' ? s : s.replace(/\s*(AM|PM)$/i, '');
}

/** "AM" or "PM"; French carries no meridiem. */
function meridiem(d: Date, lang: Lang): string {
  return lang === 'fr' ? '' : d.getHours() < 12 ? 'AM' : 'PM';
}

/** One time with its zone: "8:15 AM ET", "8 h 15 HE". */
export function timeWithZone(iso: string, lang: Lang): string {
  const d = new Date(iso);
  return [bare(d, lang), meridiem(d, lang), tzLabel(lang)].filter(Boolean).join(' ');
}

/**
 * A start/end range split over two lines:
 *   ["8:15 –", "9:00 AM ET"]
 * A range crossing noon labels both halves:
 *   ["11:30 AM –", "12:15 PM ET"]
 */
export function timeRangeLines(startIso: string, endIso: string, lang: Lang): [string, string] {
  const start = new Date(startIso);
  const startMeridiem = meridiem(start, lang);
  const endMeridiem = meridiem(new Date(endIso), lang);
  const showStartMeridiem = startMeridiem && startMeridiem !== endMeridiem;
  return [
    showStartMeridiem ? `${bare(start, lang)} ${startMeridiem} –` : `${bare(start, lang)} –`,
    timeWithZone(endIso, lang)
  ];
}
